import React from 'react';
import { Rocket, ShieldCheck, Trophy, Zap } from 'lucide-react';
import { heroImage } from '../mock';
import { useLang } from '../LangContext';
import BannerCarousel from './BannerCarousel';

const Hero = () => {
  const { t } = useLang();
  return (
    <section id="home" className="relative z-10 max-w-[1400px] mx-auto px-6 pt-4 pb-10">
      <div className="grid grid-cols-[1fr_auto] md:grid-cols-2 gap-4 md:gap-8 items-center">
        {/* LEFT */}
        <div>
          <div className="inline-flex items-center gap-2 bg-white/80 border border-blue-200 rounded-full pl-2 pr-3 md:pr-4 py-1.5 shadow-sm">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center shrink-0">
              <Zap className="w-3 h-3 text-white" />
            </span>
            <span className="text-[10px] md:text-[11px] font-extrabold tracking-[0.16em] text-slate-700">{t.hero.tagline}</span>
          </div>

          <h1 className="mt-5 font-black leading-[0.95] tracking-tight text-slate-900" style={{ fontSize: 'clamp(28px, 5vw, 72px)' }}>
            {t.hero.title1}<br />
            {t.hero.title2} <span className="gradient-text-legend">{t.hero.title3}</span>
            <Zap className="inline w-8 h-8 md:w-10 md:h-10 text-amber-400 -translate-y-2" />
          </h1>

          <p className="mt-5 text-slate-600 text-[13px] md:text-[15px] max-w-xl leading-relaxed">
            {t.hero.description}
          </p>

          {/* Feature badges */}
          <div className="mt-6 glass-card px-4 md:px-6 py-4 flex items-center justify-between gap-2 md:gap-4 max-w-2xl">
            <Feature icon={<ShieldCheck className="w-5 h-5 text-blue-600" />} title={t.hero.blockchain} sub={t.hero.secure} />
            <Divider />
            <Feature icon={<Trophy className="w-5 h-5 text-amber-500" />} title={t.hero.playToEarn} sub={t.hero.realRewards} />
            <Divider />
            <Feature icon={<ShieldCheck className="w-5 h-5 text-blue-600" />} title={t.hero.fairPlay} sub={t.hero.audited} />
          </div>

          {/* Banner carousel — 16:9 contain, replaces the old JOIN PRESALE / WHITEPAPER CTA row */}
          <div className="mt-7 w-full relative">
            <div
              aria-hidden
              className="absolute inset-x-6 -bottom-3 h-10 rounded-full blur-2xl pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.65) 0%, rgba(139, 92, 246, 0.2) 55%, transparent 80%)' }}
            />
            <BannerCarousel aspectRatio="16 / 9" objectFit="contain" />
            {/* Smaller JOIN PRESALE button below the carousel */}
            <div className="mt-4">
              <a href="#presale" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-black text-[12px] tracking-[0.14em] px-4 py-2 shadow-md transition" data-testid="hero-join-presale-btn">
                <Rocket className="w-4 h-4" />
                {t.hero.joinPresale}
              </a>
            </div>
          </div>
        </div>

        {/* RIGHT - Hero image (always side by side, even on mobile) */}
        <div className="relative flex items-center justify-center w-[140px] sm:w-[220px] md:w-auto">
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(59,130,246,0.25), transparent 60%)'
          }} />
          <img
            src={heroImage}
            alt="Domia"
            className="relative w-full max-w-[640px] mx-auto float-anim drop-shadow-[0_30px_60px_rgba(30,64,175,0.45)]"
            style={{ mixBlendMode: 'screen', filter: 'contrast(1.05) saturate(1.05)' }}
          />
        </div>
      </div>
    </section>
  );
};

const Feature = ({ icon, title, sub }) => (
  <div className="flex items-center gap-2 md:gap-2.5">
    <div className="w-8 h-8 md:w-9 md:h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">{icon}</div>
    <div className="leading-tight">
      <div className="text-[10px] md:text-[11px] font-extrabold text-slate-900 tracking-wide">{title}</div>
      <div className="text-[9px] md:text-[10px] text-slate-500 font-semibold">{sub}</div>
    </div>
  </div>
);

const Divider = () => <div className="w-px h-8 bg-slate-200" />;

export default Hero;
